/**
 * `GET /api/activity/usage.php` (etl-pilot/public/api/activity/usage.php): the new app's use per
 * user and view. SUPERADMIN and TENANTADMIN only (403 otherwise). A tenant admin always gets their
 * own tenant without DistribuAI's accounts; a superadmin may pick a tenant (or all) and include them.
 * Timestamps are ISO 8601 in UTC; durations are seconds of active time.
 */
import { api } from '@/api/client'

/** The page's path in the app (a platform page, the same for every tenant). */
export const USAGE_PATH = '/usage'
export const USAGE_ROLES: readonly string[] = ['SUPERADMIN', 'TENANTADMIN']
export const USAGE_PERIODS = ['7d', '30d', '90d'] as const
export type UsagePeriod = (typeof USAGE_PERIODS)[number]

export function canSeeUsage(roleName: string | null | undefined): boolean {
    return USAGE_ROLES.includes(String(roleName ?? '').toUpperCase())
}

export interface UsageViewRef {
    page_path: string
    page_title: string | null
    route_key: string | null
}

export interface UsageUser {
    id: number
    name: string
    email: string
    enabled: boolean
    roles: string[]
    /** A DistribuAI account (staff, demo, test). */
    staff: boolean
    last_login: string | null
    /** The end of the latest visit, or the latest login if later. */
    last_seen: string | null
    logins: number
    visits: number
    sessions: number
    active_days: number
    active_seconds: number
    top_view: (UsageViewRef & { active_seconds: number; visits: number }) | null
}

export interface UsageView extends UsageViewRef {
    visits: number
    users: number
    active_seconds: number
    last_visited_at: string | null
}

export interface UsageScope {
    period: string
    tenant_id: number | null
    include_staff: boolean
    can_choose_tenant: boolean
    can_include_staff: boolean
}

export interface UsageReport {
    totals: { users: number; active_users: number; visits: number; active_seconds: number; logins: number }
    users: UsageUser[]
    views: UsageView[]
    scope: UsageScope
    /** Superadmin only: the tenants to pick from. */
    tenants?: { id: number; name: string; slug: string | null }[]
}

export interface UsageUserDetail {
    views: UsageView[]
    recent_visits: (UsageViewRef & { duration_seconds: number; visited_at: string | null })[]
    recent_logins: { at: string | null; user_agent: string | null }[]
    scope: UsageScope
}

export interface UsageQuery {
    period: UsagePeriod
    /** A tenant id or 'all'; omitted = the session's tenant. Superadmin only. */
    tenant?: string
    /** Include DistribuAI's accounts. Superadmin only. */
    staff?: boolean
}

function query(q: UsageQuery, userId?: number): string {
    const params = new URLSearchParams({ period: q.period })
    if (q.tenant) params.set('tenant', q.tenant)
    if (q.staff) params.set('staff', '1')
    if (userId !== undefined) params.set('user_id', String(userId))
    return params.toString()
}

export async function fetchUsage(q: UsageQuery, signal?: AbortSignal): Promise<UsageReport> {
    const response = await api.get<{ success: boolean; data: UsageReport }>(`/api/activity/usage.php?${query(q)}`, { signal })
    return response.data
}

export async function fetchUsageUser(q: UsageQuery, userId: number, signal?: AbortSignal): Promise<UsageUserDetail> {
    const response = await api.get<{ success: boolean; data: UsageUserDetail }>(`/api/activity/usage.php?${query(q, userId)}`, { signal })
    return response.data
}
